import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import style from './style';

export const ReportSection = styled.div<{ _margin?: string }>`
	width: 100%;
	padding: 0 calc((100% - 1080px) / 2);
	margin: ${({ _margin }) => _margin ?? '0 0 64px 0'};
`;

export const ReportTopHeader = styled.div`
	width: 100%;
	margin-bottom: 16px;
	${({ theme }) => theme.typo.body.span};

	${({ theme }) => theme.common.flexBetween};
`;

export const ReportSectionHeader = styled.h2<{ _margin?: string }>`
	${({ _margin }) => (_margin ? `margin: ${_margin}` : `margin: 0 0 16px 0`)};

	${({ theme }) => theme.typo.title.h2};
`;

export const ReportSectionText = styled.p<{
	typo?: 'span' | 'span_bold' | 'p' | 'p_bold';
	_margin?: string;
	_width?: string;
	cursor_default?: boolean;
}>`
	white-space: nowrap;
	${({ _width }) => _width && `width: ${_width}`};
	${({ _margin }) => _margin && `margin: ${_margin}`};

	${({ cursor_default }) =>
		cursor_default &&
		css`
			cursor: default;
			user-select: none;
		`}

	${({ theme, typo }) =>
		typo === 'span'
			? theme.typo.body.span
			: typo === 'span_bold'
			? theme.typo.body.span_bold
			: typo === 'p_bold'
			? theme.typo.body.p_bold
			: theme.typo.body.p};
`;

const section_ani = keyframes`
    0% {
      	opacity: 1;
    }
    50% {
		opacity: 0.5;
    }
    100% {
		opacity: 1;
    }
`;

export const ReportSectionCard = styled.div<{
	_width?: string;
	_height?: string;
	grow?: boolean;
	shrink?: boolean;
	_margin?: string;
	_padding?: string;
	animation?: boolean;
	delay?: string;
	_overflow?: 'hidden' | 'auto';
}>`
	width: ${({ _width }) => _width ?? `100%`};
	height: ${({ _height }) => _height ?? `100%`};
	${({ _margin }) => _margin && `margin: ${_margin}`};
	${({ grow }) => grow && `flex-grow: 1`};
	${({ shrink }) => shrink && `flex-shrink: 0`};
	padding: ${({ _padding }) => _padding ?? `16px 32px`};
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	${({ animation }) =>
		animation &&
		css`
			animation: ${section_ani} 2s infinite ease-in-out;
		`}
	${({ delay }) => delay && `animation-delay: ${delay}`};
	position: relative;

	${({ _overflow }) => _overflow && `overflow: ${_overflow}`};
`;

export const ReportFixedColumnChart = styled.div<{
	width?: string;
	height?: string;
	visible?: boolean;
}>`
	${({ width }) => (width ? `width:${width}` : '72px')};
	height: ${({ height }) => (height ? `${height}` : '95px')};
	background-color: ${({ theme, visible }) =>
		visible ? null : theme.colors.gray.gray15};
	${({ theme }) => theme.common.flexEnd};
	flex-direction: column;
`;

export const ReportVariableColumnChart = styled.div<{
	rate: number;
	border_radius?: string;
	width?: string;
	average?: string;
}>`
	${({ width }) => (width ? `width:${width}` : '72px')};
	height: ${({ rate }) => `${rate}%`};
	${({ border_radius }) => border_radius && `border-radius:${border_radius}`};
	background-color: ${({ theme, average }) =>
		average === 'up'
			? theme.colors.primary.primary50_key
			: average === 'down'
			? theme.colors.system.red
			: average === 'same'
			? theme.colors.gray.gray80_text
			: theme.colors.gray.gray30};
`;

export const ReportCategoryColumnChart = styled.div<{
	rate: number;
	is_highest?: boolean;
	border_radius?: string;
	width?: string;
}>`
	${({ width }) => (width ? `width:${width}` : '72px')};
	height: ${({ rate }) => `${rate}%`};
	${({ border_radius }) => border_radius && `border-radius:${border_radius}`};
	background-color: ${({ theme, is_highest }) =>
		is_highest ? theme.colors.primary.primary50_key : theme.colors.gray.gray30};
`;

export const ReportFixedColumnBar = styled.div`
	width: 56px;
	height: 100px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	${({ theme }) => theme.common.flexEnd};
	flex-direction: column;
`;

export const ReportVariableColumnBar = styled.div<{
	rate: number | boolean;
	border_radius?: string;
	average?: string | boolean;
	width?: string;
}>`
	width: ${({ width }) => (width ? `${width}` : '56px')};
	height: ${({ rate }) => `${rate}%`};
	${({ border_radius }) => border_radius && `border-radius:${border_radius}`};
	background-color: ${({ theme, average }) =>
		average === 'up'
			? theme.colors.uplus.primary50_key
			: average === 'down'
			? theme.colors.primary.primary50_key
			: average === 'same'
			? theme.colors.gray.gray80_text
			: theme.colors.gray.gray15};
`;

export const ReportContentsTypeColumnBar = styled.div<{
	rate: number | boolean;
	border_radius?: string;
	max_value?: boolean;
}>`
	width: 48px;
	height: ${({ rate }) => `${rate}%`};
	${({ border_radius }) => border_radius && `border-radius:${border_radius}`};
	background-color: ${({ theme, max_value }) =>
		max_value ? theme.colors.system.red : theme.colors.gray.gray30};
`;

export const ReportRowBar = styled.div<{
	average?: string | boolean;
}>`
	width: 232px;
	height: 0px;
	border-top: 4px solid
		${({ theme, average }) =>
			average === 'up'
				? theme.colors.system.red
				: average === 'down'
				? theme.colors.primary.primary50_key
				: average === 'same'
				? theme.colors.gray.gray80_text
				: theme.colors.gray.gray15};
	margin: 0 0 16px 0;
`;

export const ReportFixedRowBar = styled.div<{
	flex?: 'start' | 'end';
	_margin?: string;
}>`
	width: 69px;
	height: 16px;
	background-color: ${({ theme }) => theme.colors.gray.gray15};
	${({ theme }) => theme.common.flexEnd};
	/* flex-direction: column; */
	border-radius: 4px;
	${({ theme, flex }) =>
		flex === 'start' ? theme.common.flexStart : flex === 'end' && theme.common.flexEnd}
	${({ _margin }) => _margin && `margin: ${_margin}`};
`;

export const ReportVariableRowBar = styled.div<{
	rate: number | boolean;
	border_radius?: string;
	max_value?: boolean;
}>`
	width: ${({ rate }) => `${rate}%`};
	height: 16px;
	${({ border_radius }) => border_radius && `border-radius:${border_radius}`};
	background-color: ${({ theme, max_value }) =>
		max_value ? theme.colors.system.red : theme.colors.gray.gray30};
`;

export const ReportColumnBar = styled.div<{
	rate: number;
}>`
	width: 8px;
	height: ${({ rate }) => `${rate * 0.01 * 119}px`};
	background-color: ${({ theme }) => theme.colors.gray.gray15};
	border-radius: 4px;
`;

const Grid = styled(motion.div)<{
	flex?: 'start' | 'end' | 'between' | 'center';
	flex_option?: string;
	direction?: 'column' | 'row';
	align_items?: 'start' | 'end' | 'center';
	wrap?: 'wrap' | 'nowrap';
	_width?: string;
	_margin?: string;
	_padding?: string;
	_height?: string;
	align?: 'left' | 'center' | 'right';
	children_margin?: string;
	_overflow?: 'hidden';
}>`
	${({ _width }) => (_width ? `width: ${_width}` : `width: 100%`)};
	${({ _height }) => _height && `height: ${_height}`};
	${({ _margin }) => _margin && `margin: ${_margin}`};
	${({ _padding }) => _padding && `padding: ${_padding}`};
	${({ _overflow }) => _overflow === 'hidden' && `overflow: hidden`};
	${({ theme, flex }) =>
		flex === 'start'
			? theme.common.flexStart
			: flex === 'end'
			? theme.common.flexEnd
			: flex === 'between'
			? theme.common.flexBetween
			: flex === 'center' && theme.common.flexCenter};
	${({ align_items }) =>
		align_items === 'start'
			? `align-items: flex-start`
			: align_items === 'end'
			? `align-items: flex-end`
			: align_items === 'center' && `align-items: center`};
	${({ direction }) => direction === 'column' && `flex-direction: column`};
	${({ wrap }) => wrap === 'wrap' && `flex-wrap: wrap`};
	${({ flex_option }) => flex_option && `flex: ${flex_option}`};
	${({ align }) =>
		align === 'left'
			? `text-align: left`
			: align === 'right'
			? `text-align: right`
			: align === 'center' && `text-align: center`};

	gap: ${({ children_margin }) => children_margin ?? ``};
`;

type ColorType = 'gray' | 'primary' | 'black' | 'red' | 'primary_darken';

const color_condition = (color_type: ColorType) => css`
	${({ theme }) =>
		color_type === 'primary'
			? theme.colors.uplus.primary50_key
			: color_type === 'primary_darken'
			? '#FF9391'
			: color_type === 'black'
			? theme.colors.gray.gray80_text
			: color_type === 'red'
			? '#FFD2D1'
			: theme.colors.gray.gray15};
`;

export const ReportColorCircle = styled.div<{
	color_type: ColorType;
}>`
	--size: 16px;
	width: var(--size);
	height: var(--size);
	border-radius: 50%;

	background-color: ${({ color_type }) => color_condition(color_type)};
`;

export const ReportColoredText = styled(ReportSectionText)<{ color_type: ColorType }>`
	& > span {
		color: ${({ color_type }) => color_condition(color_type)};
	}
`;

export const ColoredText = styled(style.Text)<{ color: string }>`
	color: ${({ color }) => color};
`;

export const EllipsisText = styled(style.Text)`
	${({ theme }) => theme.common.ellipsis};
`;

export const EmphasizeText = styled.p`
	${({ theme }) => theme.typo.body.span};
	span {
		color: '#000000';
		${({ theme }) => theme.typo.title.h3};
	}
`;

export const SectionHorizontalRule = styled(Grid)<{ color?: string }>`
	height: ${({ _height }) => _height ?? '1px'};
	background-color: ${({ theme, color }) => (color ? color : theme.colors.gray.gray15)};
`;
